import { useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLeadForm } from "@/components/home/leadForm";
import { MessageCircle, CheckCircle2, Zap } from "lucide-react";
import pricing from "@/content/pricing.json";

const PLAN_OPTIONS = ["Aún no sé, quiero recomendación", ...pricing.plans.map((p) => p.name)];

const Field = ({ label, error, testId, ...props }) => (
  <label className="block">
    <span className="text-sm font-semibold text-brand-carbon">{label}</span>
    <input className={`mt-input mt-1.5 ${error ? "error" : ""}`} data-testid={testId} {...props} />
    {error && <span className="text-xs text-red-600 mt-1 block" data-testid={`${testId}-error`}>{error}</span>}
  </label>
);

export const RegisterModal = ({ open, event, plan, source, onClose }) => {
  const lf = useLeadForm({ event, plan }, { requireRestaurant: true });

  useEffect(() => { if (open) lf.setForm((f) => ({ ...f, event, plan: plan || PLAN_OPTIONS[0] })); }, [open, event, plan]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!open) lf.reset(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const trackEvent = event ? `modal-submit-${event}` : source === "plan" ? "modal-submit-plan" : "modal-submit";
  const onSubmit = (e) => { e.preventDefault(); lf.submit(trackEvent); };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md rounded-2xl p-0 overflow-hidden border-0 max-h-[92vh] overflow-y-auto" data-testid="register-modal">
        <div className="h-2 bg-brand-orange" />
        <div className="p-6 sm:p-8">
          {lf.status.done ? (
            <div className="text-center py-4" data-testid="register-success">
              <CheckCircle2 className="w-14 h-14 text-brand-green mx-auto mb-4" />
              <DialogTitle className="text-2xl font-heading">¡Listo, {lf.form.name.split(" ")[0]}!</DialogTitle>
              <DialogDescription className="mt-2 text-base text-gray-600">
                Te abrimos WhatsApp con tu mensaje listo. Richard te guía en minutos para dejar tu sistema funcionando.
              </DialogDescription>
              <a href={lf.status.done.url} target="_blank" rel="noopener noreferrer" className="btn-mt btn-mt-wa mt-6" data-testid="register-success-wa-link">
                <MessageCircle className="w-5 h-5" /> {lf.status.done.opened ? "Abrir WhatsApp de nuevo" : "Abrir WhatsApp"}
              </a>
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate className="space-y-4">
              <DialogHeader className="text-left space-y-1">
                <DialogTitle className="text-2xl font-heading leading-tight flex items-center gap-2"><Zap className="w-6 h-6 text-brand-orange" /> Alta express en minutos</DialogTitle>
                <DialogDescription className="text-gray-600">
                  Dinos quién eres y Richard te guía por WhatsApp. 0% de comisión en todos los planes.
                  {event && <span className="block mt-1 text-brand-orange-dark font-semibold">Evento · {event}</span>}
                </DialogDescription>
              </DialogHeader>
              <Field label="Tu nombre" placeholder="Ej. María López" value={lf.form.name} onChange={lf.set("name")} error={lf.errors.name} testId="register-name-input" autoComplete="name" />
              <Field label="Tu WhatsApp (10 dígitos)" placeholder="81 1234 5678" inputMode="numeric" value={lf.form.whatsapp} onChange={lf.set("whatsapp")} error={lf.errors.whatsapp} testId="register-whatsapp-input" autoComplete="tel" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Ciudad" placeholder="Monterrey" value={lf.form.city} onChange={lf.set("city")} error={lf.errors.city} testId="register-city-input" />
                <Field label="Nombre del restaurante" placeholder="Tacos Doña Chelo" value={lf.form.restaurant} onChange={lf.set("restaurant")} error={lf.errors.restaurant} testId="register-restaurant-input" />
              </div>
              <label className="block">
                <span className="text-sm font-semibold text-brand-carbon">Plan que te interesa</span>
                <select className="mt-input mt-1.5" value={lf.form.plan} onChange={lf.set("plan")} data-testid="register-plan-select">
                  {PLAN_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
              <input type="hidden" name="evento" value={event} data-testid="register-event-hidden" />
              <button type="submit" className="btn-mt btn-mt-primary w-full" disabled={lf.status.sending} data-testid="register-submit-button">
                <MessageCircle className="w-5 h-5" /> Activar mi restaurante
              </button>
              <p className="text-xs text-gray-500 text-center">Para el alta ten a la mano tu menú completo con precios, tu logotipo y un celular exclusivo para WhatsApp Business.</p>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RegisterModal;
